import { useEffect, useState } from "react";

import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { ModalContent } from "../components/ModalContent";
import { Button } from "@/components/ui/button";

import { Loader2, Navigation, Link2, Hash, Tag } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import { showToast } from "@/redux/Toast/actions";
import { callLoader } from "@/redux/Loader/actions";

// import {
//   insertBaseNavigation,
//   updateBaseNavigation,
// } from "@/redux/BaseNavigation/actions";

import ImageUploadField from "@/components/common/ImageUploadField";
import { Input, SelectInput } from "../components/FormControls";
import {
  createBaseNavigation,
  getBaseNavigations,
  updateBaseNavigation,
} from "@/redux/features/baseNavigation/baseNavigationThunk";
import {
  createNavigation,
  updateNavigation,
} from "@/redux/features/navigation/navigationThunk";
import { getAccountTypeDropdownList } from "@/redux/features/accountType/accountTypeSlice";

/* ---------------- INITIAL STATE ---------------- */

const initialState = {
  NavigationId: 0,
  NavigationName: "",
  NavigationURL: "",
  BaseNavigationId: 0,
  AccountTypeId: 1,
  SequenceNo: 0,
  HasSubNavigation: true,
  IsActive: true,
  CreatedBy: "Admin",
  FileData: null,
};

/* ---------------- COMPONENT ---------------- */

export default function AddBaseNavigationModal({
  open,
  onOpenChange,
  onSuccess,
  isEdit = false,
  editData = null,
}) {
  const dispatch = useDispatch();

  const baseNavigations = useSelector(
    (state) => state.baseNavigations.baseNavigationlist,
  );
  const accountTypes = useSelector((state) => state.accountType.accountTypes);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  console.log("editData: ", editData);

  /* ---------------- PREFILL ON EDIT ---------------- */

  useEffect(() => {
    if (!open) {
      setFormData(initialState);
      setErrors({});
      setPreviewImage(null);
      return;
    }

    if (isEdit && editData) {
      setPreviewImage(editData?.NavigationIcon || null);

      setFormData({
        NavigationId: editData?.NavigationId ?? 0,
        NavigationName: editData?.NavigationName ?? "",
        NavigationURL: editData?.NavigationURL ?? "",
        BaseNavigationId: editData?.BaseNavigationId ?? 0,
        AccountTypeId: editData?.AccountTypeId ?? 0,
        SequenceNo: editData?.SequenceNo ?? 0,
        HasSubNavigation: editData?.HasSubNavigation ?? true,
        IsActive: editData?.IsActive ?? true,
        CreatedBy: editData?.CreatedBy ?? "Admin",
        FileData: null,
      });

      return;
    }

    setFormData(initialState);
  }, [open, isEdit, editData]);

  useEffect(() => {
    if (!accountTypes?.length) {
      dispatch(getAccountTypeDropdownList());
    }
  }, [dispatch, accountTypes]);

  useEffect(() => {
    if (!baseNavigations?.length) {
      dispatch(
        getBaseNavigations({
          iPageNo: 1,
          iPageRecords: 1000,
        }),
      );
    }
  }, [dispatch, baseNavigations]);

  /* ---------------- HANDLERS ---------------- */

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const validate = () => {
    const err = {};

    if (!formData.NavigationName?.trim()) {
      err.NavigationName = "Navigation name is required";
    }

    if (!formData.NavigationURL?.trim()) {
      err.NavigationURL = "Navigation URL is required";
    }

    if (!formData.BaseNavigationId) {
      err.BaseNavigationId = "Base Navigation is required";
    }

    setErrors(err);

    return Object.keys(err).length === 0;
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      dispatch(callLoader(true));

      const payload = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        // Don't send empty file field
        if (key === "FileData" && !value) return;

        payload.append(key, value ?? "");
      });

      const thunk = isEdit ? updateNavigation : createNavigation;

      for (const pair of payload.entries()) {
        console.log(pair[0], pair[1]);
      }

      await dispatch(thunk(payload)).unwrap();

      dispatch(
        showToast(
          isEdit
            ? "Navigation updated successfully"
            : "Navigation created successfully",
          "success",
          true,
        ),
      );

      await onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      dispatch(
        showToast(error?.message || "Failed to save navigation", "error", true),
      );
    } finally {
      setLoading(false);
      dispatch(callLoader(false));
    }
  };
  /* ---------------- UI ---------------- */

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        {/* HEADER */}
        <DialogHeader className="relative border-b border-white/10 px-6 py-5 bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-teal-500/10 flex items-center justify-center">
              <Navigation className="w-5 h-5 text-cyan-500" />
            </div>

            <div>
              <DialogTitle className="text-white">
                {isEdit ? "Update Navigation" : "Add Navigation"}
              </DialogTitle>

              <DialogDescription className="text-slate-300">
                Manage navigation links and routing configuration.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* BODY */}
        <div className="p-6 space-y-4">
          <Input
            label="Navigation Name"
            name="NavigationName"
            icon={Navigation}
            value={formData.NavigationName}
            onValueChange={handleChange}
            error={errors.NavigationName}
          />

          <Input
            label="Navigation URL"
            name="NavigationURL"
            icon={Link2}
            value={formData.NavigationURL}
            onValueChange={handleChange}
            error={errors.NavigationURL}
          />

          <SelectInput
            label="Base Navigation"
            name="BaseNavigationId"
            icon={Navigation}
            value={formData.BaseNavigationId}
            onValueChange={handleChange}
            error={errors.BaseNavigationId}
            options={baseNavigations?.map((item) => ({
              value: item.BaseNavigationId,
              label: item.BaseNavigationName,
            }))}
          />

          <SelectInput
            label="Account Type"
            name="AccountTypeId"
            icon={Tag}
            value={formData.AccountTypeId}
            onValueChange={handleChange}
            options={
              accountTypes?.map((item) => ({
                value: item.VALUE,
                label: item.TEXT,
              })) || []
            }
          />

          {/* TOGGLES */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() =>
                handleChange("HasSubNavigation", !formData.HasSubNavigation)
              }
              className={`flex-1 p-3 rounded-lg border ${
                formData.HasSubNavigation
                  ? "border-emerald-500 bg-emerald-500/10"
                  : ""
              }`}
            >
              Has Sub Navigation
            </button>
            <button
              type="button"
              onClick={() => handleChange("IsActive", !formData.IsActive)}
              className={`flex-1 p-3 rounded-lg border ${
                formData.IsActive ? "border-emerald-500 bg-emerald-500/10" : ""
              }`}
            >
              Active
            </button>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <ImageUploadField
              label="Navigation Logo"
              value={previewImage}
              required={false}
              maxSize={5}
              accept={[
                "image/png",
                "image/jpeg",
                "image/jpg",
                "image/webp",
                "image/svg+xml",
              ]}
              hint="PNG, JPG, WEBP, SVG • Max 5MB"
              onChange={(preview, file) => {
                setPreviewImage(preview);
                handleChange("FileData", file);
              }}
            />
          </div>
        </div>

        {/* FOOTER */}
        <DialogFooter className="border-t border-slate-200 dark:border-slate-800 px-6 py-4 bg-slate-50 dark:bg-slate-900/50">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-teal-500 hover:bg-teal-600 text-white min-w-[140px]"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4 mr-2" />
                {isEdit ? "Update" : "Create"}
              </>
            )}
          </Button>
        </DialogFooter>
      </ModalContent>
    </Dialog>
  );
}
